import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Copy, MapPin, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface CopyProductDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onCopy: (options: {
    newName: string;
    targetLocationId: string;
    copyPricingRules: boolean;
    copyTranslations: boolean;
    copyInventoryItems: boolean;
  }) => Promise<void>;
  productName: string;
  currentLocationId: string;
  locations: Array<{ id: string; location_name: string }>;
  isCopying: boolean;
}

export default function CopyProductDialog({
  isOpen,
  onClose,
  onCopy,
  productName,
  currentLocationId,
  locations,
  isCopying,
}: CopyProductDialogProps) {
  const { t } = useTranslation();
  const [newName, setNewName] = useState(`${t('products.copyOf')} ${productName}`);
  const [targetLocationId, setTargetLocationId] = useState(currentLocationId);
  const [copyPricingRules, setCopyPricingRules] = useState(true);
  const [copyTranslations, setCopyTranslations] = useState(true);
  const [copyInventoryItems, setCopyInventoryItems] = useState(false);
  const [nameError, setNameError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!newName.trim()) {
      setNameError(t('products.productNameRequired'));
      return;
    }
    setNameError('');
    await onCopy({
      newName: newName.trim(),
      targetLocationId,
      copyPricingRules,
      copyTranslations,
      copyInventoryItems,
    });
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto">
      <div className="min-h-full flex items-center justify-center p-sm sm:p-md">
        <div className="bg-primary rounded-lg shadow-2xl max-w-lg w-full p-md sm:p-xl">
        {/* Header */}
        <div className="flex items-center gap-md mb-lg">
          <div className="w-12 h-12 rounded-full bg-blue/10 flex items-center justify-center">
            <Copy className="w-6 h-6 text-blue" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-primary">{t('products.copyProduct')}</h3>
            <p className="text-sm text-secondary">{t('products.copyProductDescription')}</p>
          </div>
        </div>

        {/* New Product Name */}
        <div className="space-y-lg">
          <div>
            <label htmlFor="copy-product-name" className="block text-sm font-medium text-primary mb-sm">
              {t('products.productName')} <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              id="copy-product-name"
              value={newName}
              onChange={(e) => {
                setNewName(e.target.value);
                if (nameError) setNameError('');
              }}
              className={`input w-full ${nameError ? 'border-danger' : ''}`}
              disabled={isCopying}
            />
            {nameError && (
              <p className="text-danger text-xs mt-xs">{nameError}</p>
            )}
          </div>

          {/* Target Location */}
          <div>
            <label htmlFor="copy-target-location" className="block text-sm font-medium text-primary mb-sm">
              {t('products.targetLocation')}
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" />
              <select
                id="copy-target-location"
                value={targetLocationId}
                onChange={(e) => setTargetLocationId(e.target.value)}
                className="input pl-10 w-full"
                disabled={isCopying}
              >
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.location_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Copy Options */}
          <div>
            <label className="block text-sm font-medium text-primary mb-md">
              {t('products.copyOptions')}
            </label>
            <div className="space-y-sm">
              <label className="flex items-center gap-sm cursor-pointer hover:bg-secondary p-sm rounded-md -mx-sm">
                <input
                  type="checkbox"
                  checked={copyPricingRules}
                  onChange={(e) => setCopyPricingRules(e.target.checked)}
                  className="w-4 h-4 rounded border-primary text-blue focus:ring-blue"
                  disabled={isCopying}
                />
                <div>
                  <span className="text-sm text-primary">{t('products.copyPricingRules')}</span>
                  <p className="text-xs text-secondary">{t('products.copyPricingRulesHint')}</p>
                </div>
              </label>

              <label className="flex items-center gap-sm cursor-pointer hover:bg-secondary p-sm rounded-md -mx-sm">
                <input
                  type="checkbox"
                  checked={copyTranslations}
                  onChange={(e) => setCopyTranslations(e.target.checked)}
                  className="w-4 h-4 rounded border-primary text-blue focus:ring-blue"
                  disabled={isCopying}
                />
                <div>
                  <span className="text-sm text-primary">{t('products.copyTranslations')}</span>
                  <p className="text-xs text-secondary">{t('products.copyTranslationsHint')}</p>
                </div>
              </label>

              <label className="flex items-center gap-sm cursor-pointer hover:bg-secondary p-sm rounded-md -mx-sm">
                <input
                  type="checkbox"
                  checked={copyInventoryItems}
                  onChange={(e) => setCopyInventoryItems(e.target.checked)}
                  className="w-4 h-4 rounded border-primary text-blue focus:ring-blue"
                  disabled={isCopying}
                />
                <div>
                  <span className="text-sm text-primary">{t('products.copyInventoryItems')}</span>
                  <p className="text-xs text-secondary">{t('products.copyInventoryItemsHint')}</p>
                </div>
              </label>
            </div>
          </div>

          {/* Info about hidden state */}
          <div className="bg-blue/5 border border-blue/20 rounded-lg p-md">
            <p className="text-xs text-secondary">
              {t('products.copyHiddenNote')}
            </p>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="flex items-center justify-end gap-md mt-lg">
          <button
            type="button"
            onClick={onClose}
            disabled={isCopying}
            className="btn btn-ghost"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isCopying}
            className="btn btn-primary"
          >
            {isCopying ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('products.copyingProduct')}
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                {t('products.createCopy')}
              </>
            )}
          </button>
        </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
